import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';

const EMPTY = { name_en:'', name_ar:'', notes:'' };

export default function CompanyDocsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [newFiles, setNewFiles] = useState([]);
  const fileInputRef = useRef(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const { data, isLoading } = useQuery({
    queryKey:['company-docs'],
    queryFn:()=>api.get('/company-docs').then(r=>r.data),
  });

  const readFile = file => new Promise(resolve=>{
    const r=new FileReader();
    r.onload=e=>resolve({name:file.name,data:e.target.result,size:file.size});
    r.readAsDataURL(file);
  });

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    const read = await Promise.all(files.map(readFile));
    setNewFiles(p=>[...p,...read]);
    e.target.value='';
  };

  const removeNewFile = idx => setNewFiles(p=>p.filter((_,i)=>i!==idx));

  const createMut = useMutation({
    mutationFn: async () => {
      if (!form.name_en) throw new Error('English name required');
      return api.post('/company-docs', {...form, files:newFiles});
    },
    onSuccess:()=>{ qc.invalidateQueries(['company-docs']); setShowForm(false); setForm(EMPTY); setNewFiles([]); toast.success('Document added!'); },
    onError: e=>toast.error(e.message||e.response?.data?.message||'Error'),
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      if (!form.name_en) throw new Error('English name required');
      return api.put('/company-docs/'+editing.id, {...form, files:newFiles});
    },
    onSuccess:()=>{ qc.invalidateQueries(['company-docs']); setEditing(null); setNewFiles([]); toast.success('Updated!'); },
    onError: e=>toast.error(e.message||e.response?.data?.message||'Error'),
  });

  const deleteMut = useMutation({
    mutationFn: id=>api.delete('/company-docs/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['company-docs']); toast.success('Deleted'); },
  });

  const deleteFileMut = useMutation({
    mutationFn: fileId=>api.delete('/company-docs/files/'+fileId),
    onSuccess:()=>{ qc.invalidateQueries(['company-docs']); toast.success('File removed'); },
  });

  const downloadFile = async (fileId, fileName) => {
    try {
      const res = await api.get('/company-docs/files/'+fileId+'/download',{responseType:'blob'});
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href=url; a.download=fileName; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('File not found'); }
  };

  const openEdit = doc => {
    setEditing(doc);
    setForm({ name_en:doc.name_en, name_ar:doc.name_ar||'', notes:doc.notes||'' });
    setNewFiles([]);
    setShowForm(false);
  };

  const docs = data?.data || [];

  const formatSize = bytes => bytes > 1024*1024
    ? (bytes/1024/1024).toFixed(1)+' MB'
    : (bytes/1024).toFixed(1)+' KB';

  const getFileIcon = name => {
    const ext = name.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼️';
    if (['doc','docx'].includes(ext)) return '📝';
    if (['xls','xlsx'].includes(ext)) return '📊';
    return '📎';
  };

  return (
    <div>
      <div className="g3" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Documents</div><div className="stat-val">{docs.length}</div></div>
        <div className="card-sm"><div className="stat-label">Total Files</div><div className="stat-val" style={{color:'var(--green)'}}>{docs.reduce((s,d)=>s+(d.files?.length||0),0)}</div></div>
        <div className="card-sm"><div className="stat-label">Last Updated</div><div className="stat-val" style={{fontSize:16}}>{docs[0]?new Date(docs[0].updatedAt).toLocaleDateString():'--'}</div></div>
      </div>

      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button className="btn btn-primary" onClick={()=>{setShowForm(v=>!v);setEditing(null);setForm(EMPTY);setNewFiles([]);}}>+ Add Document</button>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit Document':'New Company Document'}</div>
          <div className="form-grid" style={{marginBottom:16}}>
            <div className="form-group"><label className="form-label">English Name *</label>
              <input className="form-input" value={form.name_en} onChange={e=>set('name_en',e.target.value)} placeholder="Document name in English"/></div>
            <div className="form-group"><label className="form-label">Arabic Name</label>
              <input className="form-input" value={form.name_ar} onChange={e=>set('name_ar',e.target.value)} placeholder="اسم المستند بالعربية" dir="rtl" style={{textAlign:'right'}}/></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Notes..."/></div>
          </div>

          {/* Upload Area */}
          <div style={{marginBottom:16}}>
            <label className="form-label" style={{marginBottom:8,display:'block'}}>Upload Attachments</label>
            <div
              onClick={()=>fileInputRef.current?.click()}
              style={{border:'2px dashed var(--border)',borderRadius:12,padding:24,textAlign:'center',cursor:'pointer',background:'var(--surface2)',transition:'.2s'}}
              onMouseOver={e=>e.currentTarget.style.borderColor='var(--green)'}
              onMouseOut={e=>e.currentTarget.style.borderColor='var(--border)'}
            >
              <div style={{fontSize:28,marginBottom:8}}>📎</div>
              <div style={{fontWeight:600,color:'var(--text)',marginBottom:4}}>Click to upload files</div>
              <div style={{fontSize:12,color:'var(--muted)'}}>PDF, Word, Excel, Images — Multiple files supported</div>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" onChange={handleFileChange} style={{display:'none'}}/>
            </div>

            {/* New files preview */}
            {newFiles.length>0 && (
              <div style={{marginTop:12}}>
                <div style={{fontSize:12,fontWeight:600,color:'var(--muted)',marginBottom:8}}>New files to upload ({newFiles.length}):</div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {newFiles.map((f,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'center',gap:10,background:'var(--green-light)',border:'1px solid #86efac',borderRadius:8,padding:'8px 12px'}}>
                      <span style={{fontSize:18}}>{getFileIcon(f.name)}</span>
                      <span style={{flex:1,fontSize:13,fontWeight:500}}>{f.name}</span>
                      <span style={{fontSize:11,color:'var(--muted)'}}>{formatSize(f.size)}</span>
                      <button onClick={()=>removeNewFile(i)} style={{background:'none',border:'none',color:'var(--danger)',cursor:'pointer',fontSize:16}}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Existing files when editing */}
            {editing && editing.files && editing.files.length>0 && (
              <div style={{marginTop:12}}>
                <div style={{fontSize:12,fontWeight:600,color:'var(--muted)',marginBottom:8}}>Existing files:</div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {editing.files.map(f=>(
                    <div key={f.id} style={{display:'flex',alignItems:'center',gap:10,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px'}}>
                      <span style={{fontSize:18}}>{getFileIcon(f.file_name)}</span>
                      <span style={{flex:1,fontSize:13,fontWeight:500}}>{f.file_name}</span>
                      <span style={{fontSize:11,color:'var(--muted)'}}>{f.file_size}</span>
                      <button className="btn btn-outline btn-sm" style={{color:'var(--green)',borderColor:'var(--green)'}} onClick={()=>downloadFile(f.id,f.file_name)}>Download</button>
                      <button onClick={()=>{if(window.confirm('Remove file?'))deleteFileMut.mutate(f.id);}} style={{background:'none',border:'none',color:'var(--danger)',cursor:'pointer',fontSize:16}}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" onClick={()=>editing?updateMut.mutate():createMut.mutate()}>
              {editing?'Update Document':'Save Document'}
            </button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);setNewFiles([]);}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Documents List */}
      {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {docs.length===0&&<div className="card"><div className="empty-state">No company documents yet</div></div>}
          {docs.map(doc=>(
            <div key={doc.id} className="card">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:'var(--text)',marginBottom:2}}>{doc.name_en}</div>
                  {doc.name_ar&&<div style={{fontSize:14,color:'var(--muted)',direction:'rtl',textAlign:'right'}}>{doc.name_ar}</div>}
                  {doc.notes&&<div style={{fontSize:12,color:'var(--muted)',marginTop:4}}>{doc.notes}</div>}
                </div>
                <div style={{display:'flex',gap:6,flexShrink:0,marginLeft:16}}>
                  <span className="badge badge-muted" style={{fontSize:11}}>{doc.files?.length||0} files</span>
                  <button className="btn btn-outline btn-sm" onClick={()=>openEdit(doc)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete document and all files?'))deleteMut.mutate(doc.id);}}>Delete</button>
                </div>
              </div>

              {doc.files && doc.files.length>0 && (
                <div style={{borderTop:'1px solid var(--border)',paddingTop:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>Attachments</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    {doc.files.map(f=>(
                      <div key={f.id} style={{display:'flex',alignItems:'center',gap:8,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:'7px 12px'}}>
                        <span style={{fontSize:16}}>{getFileIcon(f.file_name)}</span>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>{f.file_name}</div>
                          <div style={{fontSize:10,color:'var(--muted)'}}>{f.file_size}</div>
                        </div>
                        <button className="btn btn-outline btn-sm" style={{color:'var(--green)',borderColor:'var(--green)',marginLeft:4}} onClick={()=>downloadFile(f.id,f.file_name)}>
                          ↓ Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
